import { useQuery } from '@tanstack/react-query';
import type {
  TargetTunnelRequirementTargetType,
  TunnelRequirement,
} from '@terra/shared-contracts-internal-network-tunnel';
import { internalNetworkTunnelClient } from '@terra/shared-rest-clients-internal-network-tunnel';
import { createApiHeaders } from '../../clients/tester-client/api-core';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';

export const TARGET_TUNNEL_REQUIREMENT_QUERY_KEY =
  'query.target-tunnel-requirement';

export const useTargetTunnelRequirement = ({
  targetType,
  targetId,
  enabled = true,
}: {
  targetType?: TargetTunnelRequirementTargetType | null;
  targetId?: string | null;
  enabled?: boolean;
}) => {
  const isQueryEnabled = Boolean(enabled && targetType && targetId);

  const { data, isLoading, isError, error, refetch, isFetched } = useQuery({
    queryKey: [TARGET_TUNNEL_REQUIREMENT_QUERY_KEY, targetType, targetId],
    queryFn: async (): Promise<TunnelRequirement> => {
      if (!targetType || !targetId) {
        return { kind: 'notRequired' };
      }

      const headers = await createApiHeaders();
      const response = await internalNetworkTunnelClient.getTargetRequirement({
        query: { targetType, targetId },
        extraHeaders: headers,
        overrideClientOptions: { baseUrl: getCurrentGatewayUrl() },
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch target tunnel requirement');
      }

      return response.body;
    },
    enabled: isQueryEnabled,
  });

  return {
    requirement: data ?? null,
    isLoading,
    isError,
    error,
    refetch,
    isFetched,
  };
};
